/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback } from 'react';

import { IChangePasswordFormValues } from '../../pages/user-profile-general-info/ChangePasswordModal.container';

import { UsePasswordUpdateParams, UsePasswordUpdateReturnType } from './usePasswordUpdate.decl';

import {
  UpdateUserPasswordMutation,
  UpdateUserPasswordMutationVariables,
} from '@generated/UpdateUserPasswordMutation';
import { UPDATE_USER_PASSWORD_MUTATION } from '@graphql/mutations/UpdateUserPasswordMutation';
import { ClientException, handleFormErrors } from '@helpers/errors.helpers';
import { useGraphMutation } from '@hooks/useMutation';
import { useTracker } from '@hooks/useTracker/useTracker';

/**
 * Hook to update a user's password given a handler when the modal is hidden.
 * @param usePasswordUpdateParams - destructured parameter
 * @returns an object holding a function responsible for updating a user's password, and its states
 */
export function usePasswordUpdate({
  onModalHide,
}: UsePasswordUpdateParams): UsePasswordUpdateReturnType {
  const { track } = useTracker();

  const [mutate, { loading }] = useGraphMutation<
    UpdateUserPasswordMutation,
    UpdateUserPasswordMutationVariables
  >(UPDATE_USER_PASSWORD_MUTATION);

  const onSubmit = useCallback(
    async (updatePasswordInput: IChangePasswordFormValues) => {
      try {
        await mutate({ variables: { updatePasswordInput } });
        onModalHide();
        track({ event: 'password_updated' });

        return null;
      } catch (e) {
        const err = e as ClientException;

        track({
          event: 'password_update_rejected',
          payload: {
            error: `type: ${err.error.type}, message: ${err.error.message}`,
          },
        });

        return handleFormErrors(err, { keyMapper: { invalid: 'password' } });
      }
    },
    [mutate, onModalHide, track]
  );

  return {
    onSubmit,
    loading,
  };
}
